import { getLive } from '@/lib/liveContent';

export const CONTACT = {
  phone: '046 927 4424',
  phoneHref: 'tel:+353469274424',
  email: 'info@amden.ie',
  emailHref: 'mailto:info@amden.ie',
  address: 'Unit 39, Kells Business Park, Kells, Co. Meath, A82 PA44',
  locationLine: 'Kells, Co. Meath, Ireland',
};

export const SOCIALS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/80525242/', icon: 'linkedin' },
  { label: 'Facebook', href: 'https://www.facebook.com/AmdenGroup', icon: 'facebook' },
  { label: 'Instagram', href: 'https://www.instagram.com/theamdengroup/?hl=en', icon: 'instagram' },
];

export const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Services', href: '/services', hasDropdown: true },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Ecosystem', href: '/ecosystem' },
  { label: 'Insights', href: '/insights' },
  { label: 'Contact', href: '/contact' },
];

export const SERVICES = [
  {
    id: 'operations',
    name: 'Amden Operations',
    color: '#FFB800',
    description:
      'Practical operational structure, systems and accountability that help your business run efficiently, day in and day out.',
    areas: ['Systems & process design', 'Operational accountability', 'Performance visibility'],
    href: '/services/operations',
  },
  {
    id: 'people',
    name: 'Amden People',
    color: '#D6318B',
    description:
      'Recruitment, HR and team development that strengthens how you hire, manage and grow your people.',
    areas: ['Recruitment & selection', 'HR structure & policy', 'Team development'],
    href: '/services/people',
  },
  {
    id: 'growth',
    name: 'Amden Growth',
    color: '#029E88',
    description:
      'Brand, marketing and sales support that builds visibility, pipeline and long-term commercial growth.',
    areas: ['Brand & positioning', 'Marketing delivery', 'Sales pipeline'],
    href: '/services/growth',
  },
  {
    id: 'launch',
    name: 'Amden Launch',
    color: '#4225C4',
    description:
      'Structured, hands-on support to turn a business idea into a market-ready operation — the right way.',
    areas: ['Business planning', 'Go-to-market setup', 'Systems from day one'],
    href: '/services/launch',
  },
];

export const AUDIENCES = [
  {
    id: 'smes',
    label: 'SMEs',
    tagline: 'Practical support without the overhead',
    heading: 'Hands-on support for ambitious SMEs',
    description:
      'From owner-managed businesses to growing teams, we help SMEs put real structure around operations, people and growth — without big-consultancy cost or complexity.',
    points: [
      'Streamlined day-to-day operations and clear accountability',
      'Recruitment and HR foundations that scale with you',
      'Marketing and sales activity that builds a real pipeline',
    ],
    cta: { label: 'Explore support for SMEs', href: '/services' },
  },
  {
    id: 'established',
    label: 'Established Businesses',
    tagline: 'Modernise how the business runs',
    heading: 'Fresh thinking for established businesses',
    description:
      'For businesses with history and strong teams, we bring an outside perspective — modernising systems, sharpening processes and unlocking the next phase of growth.',
    points: [
      'Operational reviews that find efficiency gains',
      'Leadership structure and succession planning',
      'Brand and marketing refresh for a changing market',
    ],
    cta: { label: 'See how we modernise operations', href: '/services#operations' },
  },
  {
    id: 'larger-organisations',
    label: 'Larger Organisations',
    tagline: 'Specialist project-based support',
    heading: 'Specialist capacity for larger organisations',
    description:
      'We work alongside leadership teams in larger organisations on defined projects — from operational change programmes to recruitment drives and growth initiatives.',
    points: [
      'Defined project scopes with clear deliverables',
      'Senior, hands-on consultants — not junior teams',
      'Systems and partner implementation support',
    ],
    cta: { label: 'Discuss a project with us', href: '/contact' },
  },
  {
    id: 'startups',
    label: 'Startups',
    tagline: 'Launch the right way, first time',
    heading: 'Structured launches for new ventures',
    description:
      'Amden Launch turns a promising idea into a structured, market-ready business — with the planning, systems and brand foundations in place from day one.',
    points: [
      'Business planning and go-to-market setup',
      'Brand, website and launch marketing',
      'Operational systems built in from day one',
    ],
    cta: { label: 'Start your launch plan', href: '/services#launch' },
  },
];

export const TESTIMONIALS = [
  {
    quote:
      'Amden Group provided a fresh and professional approach to both our operational processes and overall business support. Their attention to detail, communication, and ability to deliver tailored solutions made a significant impact on our day-to-day operations. A reliable team that genuinely cares about client success.',
    name: 'Michael Clarke',
    company: 'Ramor Renewables',
  },
  {
    quote:
      'The team at Amden Group were professional, proactive, and highly efficient in supporting our business requirements. They understood exactly what we needed and delivered solutions that improved both workflow and communication internally. Great people to deal with and extremely dependable.',
    name: 'Jonathan Rogers',
    company: 'Contech DPD',
  },
  {
    quote:
      'Amden Group helped us strengthen both our business operations and overall brand presence. Their support was practical, straightforward, and tailored to suit our business needs. The team were approachable, knowledgeable, and always available when needed.',
    name: 'Dermot Kerins',
    company: 'Outlook Gardens',
  },
  {
    quote:
      'Working with Amden Group brought real structure and momentum to our operations. Their team took the time to understand our business and delivered practical solutions that genuinely improved efficiency and communication across the company. Professional, responsive, and easy to work with from start to finish.',
    name: 'Simon Murray',
    company: 'Greenville IDC',
  },
  {
    quote:
      'Amden Group provided a fresh and professional approach to both our operational processes and overall business support. Their attention to detail, communication, and ability to deliver tailored solutions made a significant impact on our day-to-day operations. A reliable team that genuinely cares about client success.',
    name: 'Ger Kerwin',
    company: 'Kemmlit Ireland',
  },
];

// Article data has moved to the central CMS-ready source in
// src/data/insights.js — this file no longer keeps its own copy.
export const CLIENTS = [
  { name: 'Outlook Gardens', className: 'font-serif italic font-semibold' },
  { name: 'Ramor Renewables', className: 'font-extrabold tracking-tight' },
  { name: 'Contech DPD', className: 'font-mono uppercase tracking-widest !text-base' },
  { name: 'KGM Electrical', className: 'font-bold uppercase tracking-wide' },
  { name: 'Greenville IDC', className: 'font-serif font-medium' },
  { name: 'Kemmlit Ireland', className: 'font-semibold tracking-tight' },
];

export const HERO_IMAGE = {
  src: '/assets/hero-team-meeting.jpg',
  alt: 'The Amden Group team reviewing a client compliance dashboard together around a meeting table',
};

// ── Live CMS getters (single source of truth: the database) ─────────────
// These return published CMS content when the public API has loaded, and
// the approved bundled content as a logged fallback during the transition.
export const getContact = () => getLive('bootstrap')?.settings?.contact || CONTACT;
export const getSocials = () => getLive('bootstrap')?.settings?.socials || SOCIALS;
export const getNavLinks = () => getLive('bootstrap')?.navigation?.header || NAV_LINKS;
export const getFooterCompany = () => getLive('bootstrap')?.navigation?.footerCompany || null;
export const getFooterServices = () => getLive('bootstrap')?.navigation?.footerServices || null;
export const getTestimonials = () => getLive('testimonials') || TESTIMONIALS;
export const getClients = () => getLive('clientLogos') || CLIENTS;
